"use client"

import React, { useEffect, useRef, useState } from 'react'
import { Pill } from './Pill'
import { Prism } from './Prism'
import { PlanStatusCard } from './PlanStatusCard'
import { SuitcaseIcon } from './Icons'

interface RayPath { x1: number; y1: number; x2: number; y2: number }

interface CardConfig {
  title: string
  planValue: string
  statusValue: string
  icon?: React.ReactNode
}

interface PillsToPrismVerticalProps {
  pills: readonly string[]
  intro: string
  strategist: string
  frontCard: CardConfig
  backLeftCard: CardConfig
  backRightCard: CardConfig
}

export function PillsToPrismVertical({ pills, intro, strategist, frontCard, backLeftCard, backRightCard }: PillsToPrismVerticalProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const prismRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const pillRefs = useRef<(HTMLDivElement | null)[]>([])
  const [paths, setPaths] = useState<RayPath[]>([])
  const [beam, setBeam] = useState<RayPath | null>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const update = () => {
      if (!containerRef.current || !prismRef.current) return
      const c = containerRef.current.getBoundingClientRect()
      const prismSvg = prismRef.current.querySelector('svg') as SVGSVGElement | null
      const center = prismSvg?.getBoundingClientRect() ?? prismRef.current.getBoundingClientRect()
      const cx = center.left + center.width / 2 - c.left
      const cy = center.top + center.height / 2 - c.top
      const newPaths: RayPath[] = []
      pillRefs.current.forEach((el) => {
        if (!el) return
        const r = el.getBoundingClientRect()
        const x1 = r.left + r.width / 2 - c.left
        const y1 = r.top + r.height - c.top // from bottom of pill
        newPaths.push({ x1, y1, x2: cx, y2: cy })
      })
      setPaths(newPaths)

      // Compute thick beam from prism to main card (connects to top of card)
      if (cardRef.current) {
        const cr = cardRef.current.getBoundingClientRect()
        const bx2 = cr.left + cr.width / 2 - c.left
        const by2 = cr.top - c.top // to the top edge of the card
        setBeam({ x1: cx, y1: cy, x2: bx2, y2: by2 })
      }
    }
    update()
    const ro = new ResizeObserver(update)
    if (containerRef.current) ro.observe(containerRef.current)
    if (prismRef.current) ro.observe(prismRef.current)
    if (cardRef.current) ro.observe(cardRef.current)
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, { passive: true })
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update)
    }
  }, [pills])

  // Intersection observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect() // Only trigger once
        }
      },
      { threshold: 0.3 } // Trigger when 30% visible
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center gap-3 relative" style={{ minHeight: 420 }}>
      {/* Pills grid */}
      <div className="flex flex-wrap justify-center gap-3" style={{ maxWidth: 380, zIndex: 1 }}>
        {pills.map((label, i) => (
          <div
            key={i}
            ref={(el) => { pillRefs.current[i] = el }}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(8px)',
              transition: `opacity 0.6s ease-out ${0.2 + i * 0.2}s, transform 0.6s ease-out ${0.2 + i * 0.2}s`
            }}
          >
            <Pill size="sm">{label}</Pill>
          </div>
        ))}
      </div>

      {/* Thin rays from pills to prism */}
      <svg 
        className="absolute inset-0 pointer-events-none" 
        width="100%" 
        height="100%" 
        style={{ 
          zIndex: 0,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-out 4s'
        }}
      >
        {paths.map((p, idx) => (
          <g key={idx}>
            <defs>
              <linearGradient id={`miniRay-${idx}`} gradientUnits="userSpaceOnUse" x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2}>
                <stop offset="0%" stopColor="#88C7DB" stopOpacity="0.75" />
                <stop offset="100%" stopColor="#FAF9F5" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={`url(#miniRay-${idx})`} strokeWidth={5} strokeLinecap="round" />
          </g>
        ))}
      </svg>

      {/* Thick green beam from prism to card */}
      <svg 
        className="absolute inset-0 pointer-events-none" 
        width="100%" 
        height="100%" 
        style={{ 
          zIndex: 0,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.8s ease-out 4.2s'
        }}
      >
        {beam ? (
          <>
            <defs>
              <linearGradient id="mobileBeam" gradientUnits="userSpaceOnUse" x1={beam.x1} y1={beam.y1} x2={beam.x2} y2={beam.y2}>
                <stop offset="0%" stopColor="#FAF9F5" />
                <stop offset="100%" stopColor="#C5ECAC" />
              </linearGradient>
            </defs>
            <line x1={beam.x1} y1={beam.y1} x2={beam.x2} y2={beam.y2} stroke="url(#mobileBeam)" strokeWidth={14} strokeLinecap="round" />
          </>
        ) : null}
      </svg>

      {/* Prism */}
      <div 
        ref={prismRef} 
        style={{ 
          transform: 'scale(0.6)', 
          zIndex: 1,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.6s ease-out 2.0s'
        }}
      >
        <Prism size={140} />
      </div>

      {/* Layered cards container */}
      <div 
        style={{ 
          position: 'relative', 
          width: 280, 
          height: 200, 
          marginTop: '-48px',
          zIndex: 1
        }}
      >
        {/* Left back card */}
        <div 
          style={{ 
            position: 'absolute', 
            left: -80, 
            top: 40, 
            transform: 'rotate(-10deg) scale(0.6)', 
            transformOrigin: 'center', 
            zIndex: 1, 
            opacity: isVisible ? 0.9 : 0,
            transition: 'opacity 0.6s ease-out 2.8s'
          }}
        >
          <PlanStatusCard
            title={backLeftCard.title}
            icon={backLeftCard.icon}
            planValue={backLeftCard.planValue}
            statusValue={backLeftCard.statusValue}
            size="sm"
          />
        </div>
        
        {/* Right back card */}
        <div 
          style={{ 
            position: 'absolute', 
            right: -80, 
            top: 40, 
            transform: 'rotate(10deg) scale(0.6)', 
            transformOrigin: 'center', 
            zIndex: 1, 
            opacity: isVisible ? 0.9 : 0,
            transition: 'opacity 0.6s ease-out 2.8s'
          }}
        >
          <PlanStatusCard
            title={backRightCard.title}
            icon={backRightCard.icon}
            planValue={backRightCard.planValue}
            statusValue={backRightCard.statusValue}
            size="sm"
          />
        </div>
        
        {/* Front primary card */}
        <div 
          ref={cardRef}
          style={{ 
            position: 'absolute', 
            left: '50%', 
            top: 0, 
            transform: 'translateX(-50%) scale(0.8)', 
            transformOrigin: 'center',
            zIndex: 2,
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.6s ease-out 2.6s'
          }}
        >
          <PlanStatusCard
            title={frontCard.title}
            icon={frontCard.icon}
            planValue={frontCard.planValue}
            statusValue={frontCard.statusValue}
            size="sm"
          />
        </div>
      </div>
    </div>
  )
}
